import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";

// Regex Constants
const US_PHONE_REGEX = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const US_ZIP_CODE_REGEX = /^[0-9]{5}(-[0-9]{4})?$/;
const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
]; // Valid US states

// Utility Functions
export const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/\D/g, "");
  if (phoneNumber.length <= 3) return phoneNumber;
  if (phoneNumber.length <= 6)
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

export const isValidPhoneNumber = (phoneNumber) =>
  US_PHONE_REGEX.test(phoneNumber);

// Key Contact Schema
const keyContact = yup.object().shape({
  id: yup.string(),
  name: yup.string().when("_destroy", {
    is: false,
    then: (schema) => schema.required("Required *"),
    otherwise: (schema) => schema.notRequired(),
  }),
  designation: yup.string(),
  email: yup
    .string()
    .email("Invalid email format")
    .when("_destroy", {
      is: false,
      then: (schema) => schema.required("Required *"),
      otherwise: (schema) => schema.notRequired(),
    }),
  phone: yup
    .string()
    .matches(US_PHONE_REGEX, "Phone number is not valid *")
    .nullable(),
  primary: yup.boolean(),
  _destroy: yup.boolean(),
});

// Organization Form Schema
export const OrganizationAddSchema = yup.object().shape({
  name: yup.string().required("Required *"),
  owner: yup.string().required("Required *"),
  organization_type: yup.string().required("Required *"),
  email: yup
    .string()
    .email("Must be a valid email")
    .max(255)
    .required("Required *"),
  phone: yup
    .string()
    .matches(US_PHONE_REGEX, "Phone number is not valid *")
    .required("Required *"),
  website: yup.string(),
  status: yup.string(),
  address_attributes: yup.object().shape({
    street: yup.string().required("Required *"),
    county: yup.string().required("Required *"),
    city: yup.string(),
    state: yup.string().required("Required *"),
    zip_code: yup.string(),
    zip_code_id: yup.string(),
    city_id: yup.string(),
    county_id: yup.string(),
    state_id: yup.string(),

    // yup
    //   .string()
    //   .matches(US_ZIP_CODE_REGEX, "ZIP Code is not valid")
    //   .required("ZIP Code is required"),
    // county: yup.string(),
    // county_id: yup.mixed(),
  }),
  organization_contacts_attributes: yup
    .array()
    .of(keyContact)
    .test(
      "at-least-one-primary",
      "At least one key contact must be marked as Primary",
      (contacts) => {
        const activeContacts =
          contacts?.filter((contact) => !contact._destroy) || [];
        if (activeContacts.length === 0) return true;
        return activeContacts.some((contact) => contact.primary);
      }
    ),
});

// Initial Values
export const KeyContactInitialValues = {
  name: "",
  designation: "",
  phone: "",
  email: "",
  primary: false,
  _destroy: false,
};

export const OrganizationInitialValues = {
  name: "",
  owner: "",
  website: "",
  email: "",
  phone: "",
  organization_type: "",
  status: "active",
  address_attributes: {
    street: "",
    city: "",
    state: "",
    // county_id: "",
    county: "",
    zip_code: "",
    city_id: "",
    county_id: "",
    state_id: "",
    zip_code_id: "",
    // counties: [],
  },
  organization_contacts_attributes: [{ ...KeyContactInitialValues }],
};

// Encounter Form Schema (corrected one)
export const EncounterFormSchema = yup.object().shape({
  encounterDate: yup
    .date()
    .required("Encounter date is required")
    .typeError("Invalid date"),
  durationStart: yup
    .string()
    .required("Start time is required")
    .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Invalid time format (HH:MM)"),
  durationEnd: yup
    .string()
    .required("End time is required")
    .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Invalid time format (HH:MM)"),
  careManager: yup.string().required("Contact is required"),
  encounterType: yup.string().required("Encounter type is required"),
  resultOfOutreach: yup.string().required("Result of outreach is required"),
  followUpDate: yup
    .date()
    .nullable()
    .notRequired()
    .typeError("Invalid date"),
  encounterDescription: yup
    .string()
    .required("Encounter description is required")
    .min(5, "Description should be at least 5 characters long"),
});

export const EncounterFormInitialValues = {
  encounterType: "",
  encounterDate: "",
  durationStart: "",
  durationEnd: "",
  resultOfOutreach: "",
  careManager: "",
  followUpDate: "",
  encounterDescription: "",
};

export const EncounterLabels = {
  encounterDate: "1. What was the Encounter Date?",
  durationStart: "2. When did the encounter start?",
  durationEnd: "3. When did the encounter end?",
  careManager: "4. What is the name of the Contact?",
  encounterType: "5. What was the type (Modality) of encounter?",
  resultOfOutreach: "6. What is the Outcome of outreach?",
  followUpDate: "7. What is the follow-up date?",
  encounterDescription: "8. Could you describe the encounter?",
};

// import * as yup from "yup";
// import { v4 as uuidv4 } from "uuid";

// export const isValidPhoneNumber = (phoneNumber) => {
//   const regex = /^(\d{3})[\s-]?(\d{3})[\s-]?(\d{4})$/; // US phone number pattern
//   return regex.test(phoneNumber) && phoneNumber.length === 10;
// };
// const keyContact = yup.object().shape({
//   id: yup.string(),
//   name: yup.string().when("_destroy", {
//     is: false,
//     then: (schema) => schema.required("Required *"),
//     otherwise: (schema) => schema.notRequired(),
//   }),
//   designation: yup.string(),
//   email: yup
//     .string()
//     .email("Invalid email format")
//     .when("_destroy", {
//       is: false,
//       then: (schema) => schema.required("Required *"),
//       otherwise: (schema) => schema.notRequired(),
//     }),
//   phone: yup.string(),
//   primary: yup.boolean(),
//   _destroy: yup.boolean(),
// });

// export const OrganizationAddSchema = yup.object().shape({
//   name: yup.string().required("Required *"),
//   owner: yup.string().required("Required *"),
//   email: yup
//     .string()
//     .email("Must be a valid email")
//     .max(255)
//     .required("Required *"),
//   phone: yup.string(),
//   website: yup.string(),
//   address_attributes: yup.object().shape({
//     street: yup.string().required("Required *"),
//     city: yup.string().required("Required *"),
//     state: yup.string().required("Required *"),
//     zip_code:  yup
//         .string()
//         .matches(/^[0-9]{5}$/, "Mailing ZIP Code must be 5 digits")
//         .required("Mailing ZIP Code is required"),
//   }),
//   organization_contacts_attributes: yup
//     .array()
//     .of(keyContact)
//     .test(
//       "at-least-one-primary",
//       "At least one key contact must be marked as Primary",
//       (contacts) => {
//         // Filter out contacts that are NOT marked for deletion
//         const activeContacts =
//           contacts?.filter((contact) => !contact._destroy) || [];

//         // If there are no active contacts, we don't enforce the primary validation
//         if (activeContacts.length === 0) {
//           return true;
//         }

//         // At least one active contact must be marked as primary
//         return activeContacts.some((contact) => contact.primary);
//       }
//     ),
// });

// export const KeyContactInitialValues = {
//   name: "",
//   designation: "",
//   phone: "",
//   email: "",
//   primary: false,
//   _destroy: false,
// };

// export const OrganizationInitialValues = {
//   name: "",
//   owner: "",
//   website: "",
//   email: "",
//   phone: "",
//   address_attributes: {
//     street: "",
//     city: "",
//     state: "",
//     zip_code: "",
//   },
//   organization_contacts_attributes: [{ ...KeyContactInitialValues }],
// };

export const COMMUNITY_COLUMNS = [
  { columnKey: "name", label: "Name", isSortable: true },
  { columnKey: "website", label: "Website", isSortable: true },
  { columnKey: "primary_contact", label: "Primary Contact", isSortable: true },
  { columnKey: "designation", label: "Contact", isSortable: true },
  {
    columnKey: "total_encounter",
    label: "Count of Encounters",
    isSortable: false,
  },
  {
    columnKey: "enrollements",
    label: "Enrollments/Referrals",
    isSortable: true,
  },
  { columnKey: "status", label: "Status", isSortable: false },
];
